import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/api';
import AppHeader from '../components/layout/AppHeader';
import SettingsTour from '../components/onboarding/SettingsTour';

const Settings = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // Profile fields
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [bio, setBio] = useState('');
    const [website, setWebsite] = useState('');
    const [location, setLocation] = useState('');
    const [title, setTitle] = useState('');
    const [availability, setAvailability] = useState('');
    const [hourlyRate, setHourlyRate] = useState('');
    const [yearsOfExperience, setYearsOfExperience] = useState('');
    const [portfolioUrl, setPortfolioUrl] = useState('');
    const [resumeUrl, setResumeUrl] = useState('');
    const [whyJoin, setWhyJoin] = useState('');
    const [goals, setGoals] = useState('');
    const [expertise, setExpertise] = useState('');

    // Avatar
    const [avatar, setAvatar] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    useEffect(() => {
        // Load user data when component mounts
        if (user) {
            setFirstName(user.firstName || '');
            setMiddleName((user as any).middleName || '');
            setLastName(user.lastName || '');
            setBio((user as any).profile?.bio || '');
            setWebsite((user as any).profile?.website || '');
            setLocation((user as any).profile?.location || '');
            setTitle((user as any).professional?.title || '');
            setAvailability((user as any).professional?.availability || '');
            setHourlyRate((user as any).professional?.hourlyRate?.toString() || '');
            setYearsOfExperience((user as any).professional?.yearsOfExperience?.toString() || '');
            setPortfolioUrl((user as any).professional?.portfolioUrl || '');
            setResumeUrl((user as any).professional?.resumeUrl || '');
            setWhyJoin((user as any).professional?.onboarding?.whyJoin || '');
            setGoals((user as any).professional?.onboarding?.goals || '');
            setExpertise((user as any).professional?.onboarding?.expertise || '');
            setAvatarPreview((user as any).avatar || (user as any).profile?.avatar || null);
        }
    }, [user]);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatar(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveAvatar = () => {
        setAvatar(null);
        setAvatarPreview(null);
    };

    const handleSaveProfile = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Upload avatar if changed
            if (avatar) {
                await apiClient.uploadAvatar(avatar);
            }

            // Update profile data
            // Update profile data
            const updates = {
                firstName,
                middleName,
                lastName,
                bio,
                website,
                location,
                title,
                availability,
                hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
                yearsOfExperience: yearsOfExperience ? parseInt(yearsOfExperience) : undefined,
                portfolioUrl,
                resumeUrl,
                whyJoin,
                goals,
                expertise
            };

            // Update both backend and local context
            await updateUser(updates);

            setSuccess('Profile updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'profile', name: 'Professional Profile', icon: '👤' },
        { id: 'security', name: 'Security', icon: '🔒' },
        { id: 'notifications', name: 'Notifications', icon: '🔔' }
    ];

    return (
        <div className="min-h-screen bg-premium-wood-dark font-sans">
            <SettingsTour />
            <AppHeader />

            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-premium-gold/70 hover:text-premium-gold mb-4 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <h1 className="text-4xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold">
                        Settings
                    </h1>
                    <p className="text-white/60 mt-2">Manage your account and preferences</p>
                </motion.div>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Tabs Sidebar */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="md:w-64 flex-shrink-0"
                    >
                        <div className="bg-black/20 border border-premium-gold/20 rounded-xl p-2 backdrop-blur-sm">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 mb-1 ${activeTab === tab.id
                                        ? 'bg-premium-gold text-premium-wood-dark font-bold'
                                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                                        }`}
                                >
                                    <span className="text-xl">{tab.icon}</span>
                                    <span>{tab.name}</span>
                                </button>
                            ))}
                        </div>
                    </motion.div>

                    {/* Content Area */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex-1"
                    >
                        <div className="bg-black/20 border border-premium-gold/20 rounded-xl p-6 backdrop-blur-sm">
                            {activeTab === 'profile' && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-bold text-premium-gold mb-6">Professional Profile</h2>

                                    {/* Avatar Section */}
                                    <div className="flex items-center gap-6 p-6 bg-white/5 rounded-xl border border-white/10">
                                        <div className="relative group">
                                            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-premium-gold/50 bg-black/40">
                                                {avatarPreview ? (
                                                    <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-white/30 text-3xl">
                                                        👤
                                                    </div>
                                                )}
                                            </div>
                                            <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleAvatarChange}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                                <span className="text-white text-sm font-bold">Change</span>
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold text-white mb-1">Profile Photo</h3>
                                            <p className="text-sm text-white/60 mb-3">Update your profile picture</p>
                                            <div className="flex gap-3">
                                                <label className="px-4 py-2 bg-premium-gold/20 hover:bg-premium-gold/30 text-premium-gold text-sm font-bold rounded-lg cursor-pointer transition-colors">
                                                    Upload Photo
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleAvatarChange}
                                                        className="hidden"
                                                    />
                                                </label>
                                                {avatarPreview && (
                                                    <button
                                                        onClick={handleRemoveAvatar}
                                                        className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-bold rounded-lg transition-colors"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Basic Info */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">Basic Information</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">First Name</label>
                                                <input
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="First Name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Middle Name</label>
                                                <input
                                                    type="text"
                                                    value={middleName}
                                                    onChange={(e) => setMiddleName(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="Middle Name (Optional)"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Last Name</label>
                                                <input
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="Last Name"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-premium-gold/90 mb-2">Bio</label>
                                            <textarea
                                                value={bio}
                                                onChange={(e) => setBio(e.target.value)}
                                                rows={4}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all resize-none"
                                                placeholder="Tell us about yourself..."
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Website</label>
                                                <input
                                                    type="url"
                                                    value={website}
                                                    onChange={(e) => setWebsite(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="https://yourwebsite.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Location</label>
                                                <input
                                                    type="text"
                                                    value={location}
                                                    onChange={(e) => setLocation(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="City, Country"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Professional Details */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">Professional Details</h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Professional Title</label>
                                                <input
                                                    type="text"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="e.g., Full-Stack Developer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Availability</label>
                                                <select
                                                    value={availability}
                                                    onChange={(e) => setAvailability(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all [&>option]:bg-[#1a1a1a] [&>option]:text-white"
                                                >
                                                    <option value="">Select...</option>
                                                    <option value="Full-time">Full-time</option>
                                                    <option value="Part-time">Part-time</option>
                                                    <option value="Contract">Contract</option>
                                                    <option value="Unavailable">Not Available</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Hourly Rate (USD)</label>
                                                <input
                                                    type="number"
                                                    value={hourlyRate}
                                                    onChange={(e) => setHourlyRate(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="50"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Years of Experience</label>
                                                <input
                                                    type="number"
                                                    value={yearsOfExperience}
                                                    onChange={(e) => setYearsOfExperience(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="5"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Portfolio URL</label>
                                                <input
                                                    type="url"
                                                    value={portfolioUrl}
                                                    onChange={(e) => setPortfolioUrl(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="https://yourportfolio.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-premium-gold/90 mb-2">Resume/CV URL</label>
                                                <input
                                                    type="url"
                                                    value={resumeUrl}
                                                    onChange={(e) => setResumeUrl(e.target.value)}
                                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all"
                                                    placeholder="https://drive.google.com/..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* About You */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">About You</h3>

                                        <div>
                                            <label className="block text-sm font-medium text-premium-gold/90 mb-2">Why did you join TechUnity?</label>
                                            <textarea
                                                value={whyJoin}
                                                onChange={(e) => setWhyJoin(e.target.value)}
                                                rows={3}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all resize-none"
                                                placeholder="Share your motivation..."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-premium-gold/90 mb-2">What are your professional goals?</label>
                                            <textarea
                                                value={goals}
                                                onChange={(e) => setGoals(e.target.value)}
                                                rows={3}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all resize-none"
                                                placeholder="Where do you see yourself..."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-premium-gold/90 mb-2">What's your area of expertise?</label>
                                            <textarea
                                                value={expertise}
                                                onChange={(e) => setExpertise(e.target.value)}
                                                rows={3}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-premium-gold/50 transition-all resize-none"
                                                placeholder="Describe your skills and experience..."
                                            />
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="flex items-center justify-end gap-4 pt-6 border-t border-white/10">
                                        {success && (
                                            <div className="text-green-400 text-sm font-medium">
                                                ✓ {success}
                                            </div>
                                        )}
                                        {error && (
                                            <div className="text-red-400 text-sm font-medium">
                                                ✗ {error}
                                            </div>
                                        )}
                                        <button
                                            onClick={handleSaveProfile}
                                            disabled={loading}
                                            className="px-8 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-bold text-premium-gold mb-6">Security Settings</h2>
                                    <p className="text-white/60">Security settings coming soon...</p>
                                </div>
                            )}

                            {activeTab === 'notifications' && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-bold text-premium-gold mb-6">Notification Preferences</h2>
                                    <p className="text-white/60">Notification settings coming soon...</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
